import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Doughnut, Bar, Line } from 'react-chartjs-2';

import { Chart, registerables, ArcElement, Tooltip, Legend } from 'chart.js';
import {
  FaSearch, FaCaretDown, FaCalendarAlt, FaFileAlt, FaCaretUp, FaWindows,
  FaApple, FaLinux, FaTable, FaClock, FaNetworkWired, FaCogs, FaUsers, FaKey,
  FaBitcoin, FaLock, FaArrowLeft, FaArrowRight, FaStar, FaStarHalfAlt, FaDiscord, FaGithub,
  FaGlobe, FaTwitter, FaReddit, FaFacebook, FaTelegram, FaMedium, FaYoutube, FaLinkedin,
  FaInstagram, FaArrowUp, FaArrowDown, FaDollarSign, FaExchangeAlt, FaHashtag, FaChartLine
} from 'react-icons/fa';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdsComponent from '../AdsComponent';
import Status from './Status';
import Sidebar from './Sidebar';
import LatestReleases from './LatestReleases';
import EventItem from './EventItem';
import VoteList from './VoteList';
import './Home.css';

Chart.register(ArcElement, Tooltip, Legend, ...registerables);

const defaultCurrencyData = {
  symbol: 'Unknown',
  name: 'Unknown',
  details: {},
  markets: [],
  priceHistory: [],
}

function Home() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [algorithms, setAlgorithms] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [hardwareTypes, setHardwareTypes] = useState([]);
  const [selectedHardwareType, setSelectedHardwareType] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [blockDistribution, setBlockDistribution] = useState({ labels: [], datasets: [] });
  const [releaseData, setReleaseData] = useState(null);
  const [error, setError] = useState(null);
  const [isDownloadsVisible, setIsDownloadsVisible] = useState(false);
  const [releases, setReleases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const blocksPerPage = 10;

  const priceHistory = (selectedCurrency?.priceHistory || []).sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );

  const initialPrice = priceHistory.length > 0 ? priceHistory[0].price : null;
  const finalPrice = priceHistory.length > 0 ? priceHistory[priceHistory.length - 1].price : null;

  const percentageChange = initialPrice && finalPrice 
  ? ((finalPrice - initialPrice) / initialPrice) * 100 
  : null;

  const isPositiveChange = percentageChange > 0;

  const lineColor = isPositiveChange ? 'green' : 'red';

  const chartData = priceHistory.length > 0 ? {
    labels: priceHistory.map((entry) => new Date(entry.timestamp).toLocaleDateString()),
    datasets: [
      {
        label: '',
        data: priceHistory.map((entry) => entry.price),
        fill: false,
        borderColor: lineColor,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
      },
    ],
  } : { labels: [], datasets: [] };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            return `Price: ${context.raw} USDT`;
          },
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
    },
  };

 useEffect(() => {
    if (!symbol) {
      // Restablecer el estado si no hay un símbolo en la URL
      setSelectedCurrency(null);
      setBlocks([]);
    }
  }, [symbol]);

  const cleanSymbol = symbol ? symbol.split(/[-_]/)[0].toUpperCase() : '';
  const fetchReleaseData = useCallback(async () => {
    //const cleanSymbol = symbol.split(/[-_]/)[0].toUpperCase();
    if (cleanSymbol) {
      try {
        const response = await axios.get(`/api/release/${cleanSymbol}`);
        if (response.data.success) {
          setReleaseData(response.data.data);
        } else {
          setError('No se encontraron datos de la moneda.');
        }
      } catch (error) {
        console.error('Error fetching release data:', error);
        setError('Error al obtener los datos de la moneda.');
      }
    }
  }, [cleanSymbol]);

  const DownloadChart = ({ assets }) => {
    const assetData = JSON.parse(assets);

    const data = {
      labels: assetData.map(asset => asset.name),
      datasets: [
        {
          label: 'Total Downloads',
          data: assetData.map(asset => asset.downloadCount),
          backgroundColor: assetData.map(() => `hsl(${Math.random() * 360}, 70%, 50%)`),
          borderColor: assetData.map(() => '#fff'),
          borderWidth: 1,
        },
      ],
    };

    const options = {
      indexAxis: 'y',
      scales: {
        x: {
          beginAtZero: true,
        },
      },
    };

    return <Bar data={data} options={options} />;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getOSIcon = (fileName) => {
    const lowerCaseName = fileName.toLowerCase();
    if (lowerCaseName.includes('win')) return <FaWindows />;
    if (lowerCaseName.includes('mac') || lowerCaseName.includes('osx')) return <FaApple />;
    if (lowerCaseName.includes('linux') || lowerCaseName.includes('ubuntu') || lowerCaseName.includes('arm')) return <FaLinux />;
    return <FaFileAlt />;
  };

  const renderLatestVersions = () => {
    const assets = JSON.parse(releaseData.assets);
    const latestAssets = assets.slice(0, 3);
    return (
      <div className="latest-versions">
        {latestAssets.map((asset, index) => (
          <a key={index} href={asset.downloadUrl} target="_blank" rel="noopener noreferrer" className="latest-version-icon">
            {getOSIcon(asset.name)}
            <span>{asset.size}</span>
          </a>
        ))}
      </div>
    );
  };

  useEffect(() => {
    axios.get('/api/releases')
      .then(response => {
        if (response.data.success) {
          setReleases(response.data.data.slice(0, 20));
        } else {
          setError('Error al obtener los datos de los lanzamientos.');
        }
      })
      .catch(error => {
        console.error('Error fetching release data:', error);
        setError('Error al obtener los datos de los lanzamientos.');
      });
  }, []);

  useEffect(() => {
    fetchReleaseData();
  }, [fetchReleaseData]);

  const handleSelectCurrency = useCallback((symbol) => {
    setSelectedCurrency(null);
    setBlocks([]);
    navigate(`/coin/${symbol}`);
// navigate(`/coin/${symbol}` , { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchCurrencyData = useCallback(() => {
    if (symbol) {
        const cleanSymbol = symbol;
      //const cleanSymbol = symbol.split(/[-_]/)[0].toUpperCase();

      axios
        .get(`/api/currency/${cleanSymbol}`)
        .then((response) => {
          const currencyData = response.data;

          if (currencyData && currencyData.symbol && currencyData.details) {
            setSelectedCurrency({
              ...currencyData,
              markets: currencyData.markets || [],
              priceHistory: currencyData.priceHistory || [],
            });
          } else {
            console.error('Incomplete data received for the currency:', cleanSymbol);
            setSelectedCurrency(defaultCurrencyData);
          }
        })
        .catch((error) => {
          console.error('Error fetching currency:', error);
          setSelectedCurrency(defaultCurrencyData);
        });

      return cleanSymbol;
    }
    return null;
  }, [symbol]);

  const fetchBlocksData = useCallback(async () => {
    if (symbol) {
      try {
        const response = await axios.get(`/api/blocks/currency/${symbol}`);
        const completedBlocks = completeMissingBlocks(response.data);
        const sortedBlocks = completedBlocks.sort((a, b) => b.block_height - a.block_height);
        setBlocks(sortedBlocks);
        calculateBlockDistribution(sortedBlocks);
      } catch (error) {
        console.error('Error fetching blocks:', error);
      }
    }
  }, [symbol]);

  useEffect(() => {
    fetchCurrencyData();
    const intervalId = setInterval(fetchCurrencyData, 120000);
    return () => clearInterval(intervalId);
  }, [fetchCurrencyData]);

  useEffect(() => {
    fetchBlocksData();
    const intervalId = setInterval(fetchBlocksData, 120000);
    return () => clearInterval(intervalId);
  }, [fetchBlocksData]);

  const fetchData = useCallback(() => {
    axios.get('/api/currencies/algorithms-hardware')
      .then(response => {
        setAlgorithms(response.data);
        const uniqueHardwareTypes = Array.from(new Set(response.data.flatMap(algo => algo.hardware_type)));
        setHardwareTypes(uniqueHardwareTypes);
      })
      .catch(error => console.error('Error fetching algorithms:', error));

    axios.get('/api/currencies/names-symbols')
      .then(response => {
        const uniqueCurrencies = filterUniqueCurrencies(response.data);
        setCurrencies(uniqueCurrencies);
        setFilteredCurrencies(uniqueCurrencies);
      })
      .catch(error => console.error('Error fetching currencies:', error));
  }, []);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 120000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const completeMissingBlocks = (blocks) => {
    if (blocks.length === 0) return [];
    const minHeight = Math.min(...blocks.map(b => b.block_height));
    const maxHeight = Math.max(...blocks.map(b => b.block_height));
    const completedBlocks = [];
    for (let i = minHeight; i <= maxHeight; i++) {
      const existingBlock = blocks.find(b => b.block_height === i);
      if (existingBlock) {
        completedBlocks.push(existingBlock);
      } else {
        completedBlocks.push({
          block_height: i,
          pool_name: 'Unknown',
          reward: 'Unknown',
          difficulty: 'Unknown',
          timestamp: 'Unknown',
        });
      }
    }
    return completedBlocks;
  };

  const filterUniqueCurrencies = (data) => {
    return data.filter((currency, index, self) =>
      index === self.findIndex((c) => c.symbol === currency.symbol)
    );
  };

  const handleSearchChange = (e) => {
    const search = e.target.value.toUpperCase();
    setSearchTerm(search);
    applyFilters(search, selectedHardwareType);
  };

  const handleHardwareTypeChange = (e) => {
    const hardware = e.target.value;
    setSelectedHardwareType(hardware);
    applyFilters(searchTerm, hardware);
  };

  const applyFilters = (search, hardware) => {
    let filtered = currencies.filter(currency =>
      currency.symbol.toUpperCase().includes(search) || currency.name.toUpperCase().includes(search)
    );

    if (hardware) {
      filtered = filtered.filter(currency => {
        const algoData = algorithms.find(algo => algo.algo === currency.algo);
        return algoData ? algoData.hardware_type.includes(hardware) : false;
      });
    }

    setFilteredCurrencies(filtered);
  };

  const getCoinImage = (symbol) => {
    const sanitizedSymbol = symbol.split(/[-_]/)[0].toUpperCase();
    return `/img/coins/${sanitizedSymbol}.webp`;
  };

  const getPoolImage = (poolName) => {
    return `/img/pools/${poolName.toLowerCase()}.webp`;
  };

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      return `${hours}h`;
    } else {
      const days = Math.floor(seconds / 86400);
      return `${days}d`;
    }
  };

  const getLatestBlockTime = useCallback((pools) => {
    if (!pools || pools.length === 0) return null;
    const latestTime = Math.min(...pools.map(pool => pool.timesincelast));
    return formatTime(latestTime);
  }, []);

  const getTimeColor = (minutes) => {
    if (minutes < 10) {
      return 'green';
    } else if (minutes >= 10 && minutes <= 30) {
      return 'yellow';
    } else {
      return 'red';
    }
  };

  const formatHashrate = (hashrate) => {
    if (typeof hashrate !== 'number' || isNaN(hashrate)) return 'N/A';
    if (hashrate >= 1e12) {
      return (hashrate / 1e12).toFixed(2) + ' TH/s';
    } else if (hashrate >= 1e9) {
      return (hashrate / 1e9).toFixed(2) + ' GH/s';
    } else if (hashrate >= 1e6) {
      return (hashrate / 1e6).toFixed(2) + ' MH/s';
    } else if (hashrate >= 1e3) {
      return (hashrate / 1e3).toFixed(2) + ' KH/s';
    } else {
      return hashrate.toFixed(2) + ' H/s';
    }
  };
  
  const calculateNetworkHashrate = useCallback((pools) => {
    return pools.reduce((total, pool) => total + pool.hashrate, 0);
  }, []);

  const renderNetworkHashrateBar = (percentage) => {
    return (
      <div className="hashrate-bar-container">
        <div
          className="hashrate-bar"
          style={{
            width: `${percentage}%`,
            backgroundColor: '#4caf50',
          }}
        ></div>
        <div className="hashrate-bar-text">
          {percentage}%
        </div>
      </div>
    );
  };

  const calculateAverageBlockTimeForPool = (blocks24h) => {
    if (!blocks24h || blocks24h <= 0) {
      return 'N/A';
    }
    const averageTimeInSeconds = (24 * 3600) / blocks24h;
    if (averageTimeInSeconds < 60) {
      return `${averageTimeInSeconds.toFixed(2)} sec`;
    } else if (averageTimeInSeconds < 3600) {
      const averageTimeInMinutes = (averageTimeInSeconds / 60).toFixed(2);
      return `${averageTimeInMinutes} min`;
    } else if (averageTimeInSeconds < 86400) {
      const averageTimeInHours = (averageTimeInSeconds / 3600).toFixed(2);
      return `${averageTimeInHours} h`;
    } else {
      const averageTimeInDays = (averageTimeInSeconds / 86400).toFixed(2);
      return `${averageTimeInDays} d`;
    }
  };

  const calculateStars = (blocks24h, avgBlockTime) => {
    if (!blocks24h || blocks24h <= 0 || avgBlockTime === 'N/A') {
      return 0;
    }
    let timeInMinutes = 0;
    if (avgBlockTime.includes('sec')) {
      timeInMinutes = parseFloat(avgBlockTime) / 60;
    } else if (avgBlockTime.includes('min')) {
      timeInMinutes = parseFloat(avgBlockTime);
    } else if (avgBlockTime.includes('h')) {
      timeInMinutes = parseFloat(avgBlockTime) * 60;
    } else if (avgBlockTime.includes('d')) {
      timeInMinutes = parseFloat(avgBlockTime) * 1440;
    }

    const blockWeight = blocks24h > 50 ? 5 : (blocks24h / 10);
    const timeWeight = timeInMinutes <= 5 ? 5 : (5 / (timeInMinutes / 5));
    const stars = Math.min(5, Math.round((blockWeight + timeWeight) / 2));

    return stars;
  };

  const getStarColor = (stars) => {
    if (stars === 5) return 'green';
    if (stars === 4) return 'limegreen';
    if (stars === 3) return 'yellow';
    if (stars === 2) return 'orange';
    if (stars === 1) return 'red';
    return 'gray';
  };

  const paginateBlocks = useCallback((blocks) => {
    const startIndex = (currentPage - 1) * blocksPerPage;
    const endIndex = startIndex + blocksPerPage;
    return blocks.slice(startIndex, endIndex);
  }, [currentPage, blocksPerPage]);

  const handleNextPage = () => {
    if (currentPage < Math.ceil(blocks.length / blocksPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getPoolNameForLastBlock = useMemo(() => {
    const lastBlockHeight = selectedCurrency ? selectedCurrency.height : null;
    if (!blocks || blocks.length === 0 || !lastBlockHeight) {
      return 'Unknown';
    }
    const block = blocks.find(block => block.block_height === lastBlockHeight);
    return block && block.pool_name ? block.pool_name : 'Unknown';
  }, [blocks, selectedCurrency]);

  const calculateBlockDistribution = (blocks) => {
    const poolBlockCount = {};

    blocks.forEach(block => {
      const poolName = block.pool_name || 'Unknown';
      if (poolBlockCount[poolName]) {
        poolBlockCount[poolName]++;
      } else {
        poolBlockCount[poolName] = 1;
      }
    });

    const labels = Object.keys(poolBlockCount);
    const data = Object.values(poolBlockCount);

    const backgroundColors = labels.map(label => {
      if (label === 'Unknown') {
        return '#FF0000';
      } else {
        return `hsl(${Math.random() * 360}, 70%, 50%)`;
      }
    });

    setBlockDistribution({
      labels,
      datasets: [
        {
          label: 'Distribución de Bloques',
          data,
          backgroundColor: backgroundColors,
          hoverBackgroundColor: backgroundColors,
        },
      ],
    });
  };

  useEffect(() => {
    fetchCurrencyData();
    fetchBlocksData();
  }, [fetchCurrencyData, fetchBlocksData]);

  return (
    <div className="home-content-container">

      <Sidebar
        searchTerm={searchTerm}
        handleSearchChange={handleSearchChange}
        hardwareTypes={hardwareTypes}
        handleHardwareTypeChange={handleHardwareTypeChange}
        filteredCurrencies={filteredCurrencies}
        handleSelectCurrency={handleSelectCurrency}
        getCoinImage={getCoinImage}
      />

      <main className="home-main-content">
        <div className="sidebar-ad">
          <AdsComponent requiredSize="728x90" />
        </div>

        <LatestReleases
          releases={releases}
          error={error}
          getCoinImage={getCoinImage}
          formatDate={formatDate}
        />

        {selectedCurrency ? (
          <div>
            <h2>
              <img
                src={getCoinImage(selectedCurrency.symbol)}
                onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                alt={selectedCurrency.name}
                className="currency-image"
              />
              {selectedCurrency.name} ({selectedCurrency.symbol})
            </h2>
            {selectedCurrency?.markets?.length > 0 ? (
            <div style={{ textAlign: 'center' }}>
              <div className="home-card">
                {selectedCurrency.markets.some((market) =>
                  market.symbol.includes('USDT')
                ) ? (
                  <div>
                    <p className="home-price">
                      {selectedCurrency.markets.find((market) =>
                        market.symbol.includes('USDT')
                      )?.lastPrice}{' '}
                      USDT
                    </p>

                    <div
                      className="home-price-range"
                      style={{
                        fontSize: '12px',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '10px',
                      }}
                    >
                      <span
                        style={{
                          color: 'green',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FaArrowUp />{' '}
                        {selectedCurrency.markets.find((market) =>
                          market.symbol.includes('USDT')
                        )?.highPrice}
                      </span>
                      <span
                        style={{
                          color: 'red',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FaArrowDown />{' '}
                        {selectedCurrency.markets.find((market) =>
                          market.symbol.includes('USDT')
                        )?.lowPrice}
                      </span>
                    </div>

                    <span
                      className={`home-change ${
                        selectedCurrency.markets
                          .find((market) => market.symbol.includes('USDT'))
                          ?.changePercent.startsWith('-')
                          ? 'home-change-negative'
                          : 'home-change-positive'
                      }`}
                    >
                      {selectedCurrency.markets
                        .find((market) => market.symbol.includes('USDT'))
                        ?.changePercent.startsWith('-') ? (
                        <FaArrowDown />
                      ) : (
                        <FaArrowUp />
                      )}
                      {` ${
                        selectedCurrency.markets.find((market) =>
                          market.symbol.includes('USDT')
                        )?.changePercent
                      }%`}
                    </span>
                  </div>
                ) : (
                  <div>
                    <p className="home-price">
                      {selectedCurrency.markets[0]?.lastPrice}{' '}
                      {selectedCurrency.markets[0]?.symbol.split('/')[1]}
                    </p>

                    <div
                      className="home-price-range"
                      style={{
                        fontSize: '12px',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '10px',
                      }}
                    >
                      <span
                        style={{
                          color: 'green',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FaArrowUp /> {selectedCurrency.markets[0]?.highPrice}{' '}
                        {selectedCurrency.markets[0]?.symbol.split('/')[1]}
                      </span>
                      <span
                        style={{
                          color: 'red',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FaArrowDown /> {selectedCurrency.markets[0]?.lowPrice}{' '}
                        {selectedCurrency.markets[0]?.symbol.split('/')[1]}
                      </span>
                    </div>

                    <span
                      className={`home-change ${
                        selectedCurrency.markets[0]?.changePercent.startsWith('-')
                          ? 'home-change-negative'
                          : 'home-change-positive'
                      }`}
                    >
                      {selectedCurrency.markets[0]?.changePercent.startsWith('-') ? (
                        <FaArrowDown />
                      ) : (
                        <FaArrowUp />
                      )}
                      {` ${selectedCurrency.markets[0]?.changePercent}%`}
                    </span>
                  </div>
                )}
              </div>

              <div className="home-links-row">
              {selectedCurrency.details && Object.entries(selectedCurrency.details).map(([key, value]) => {
                const iconMap = {
                  site: <FaGlobe size={25} />,
                  explorer: <FaSearch size={25} />,
                  discord: <FaDiscord size={25} />,
                  source: <FaGithub size={25} />,
                  twitter: <FaTwitter size={25} />,
                  facebook: <FaFacebook size={25} />,
                  reddit: <FaReddit size={25} />,
                  telegram: <FaTelegram size={25} />,
                  medium: <FaMedium size={25} />,
                  youtube: <FaYoutube size={25} />,
                  linkedin: <FaLinkedin size={25} />,
                  instagram: <FaInstagram size={25} />,
                };
                return value && iconMap[key] ? (
                  <a key={key} href={value} target="_blank" rel="noopener noreferrer" title={key.charAt(0).toUpperCase() + key.slice(1)}>
                    {iconMap[key]}
                  </a>
                ) : null;
              })}
            </div>

              <div
                style={{
                  marginTop: '20px',
                  width: '100%',
                  maxWidth: '100%',
                  height: '90px',
                  margin: '0 auto',
                }}
              >
                {priceHistory.length > 0 ? (
                  <div style={{ height: '100%', width: '100%' }}>
                    <Line data={chartData} options={chartOptions} />
                  </div>
                ) : (
                  <p>No price history available for the last 7 days.</p>
                )}
              </div>

              {percentageChange !== null && !isNaN(percentageChange) && (
                <div
                  style={{
                    marginTop: '26px',
                    fontSize: '16px',
                    color: isPositiveChange ? 'green' : 'red',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  {isPositiveChange ? <FaArrowUp /> : <FaArrowDown />}{'  '}
                  {percentageChange.toFixed(2)}% Price (Last 7 Days)
                </div>
              )}
            </div>
            ) : (
              <p>No market data available.</p>
            )}


            <VoteList symbol={selectedCurrency?.symbol} />
            <EventItem selectedCurrency={selectedCurrency} />

            {releaseData ? (
              <div className="release-details horizontal-grid">
                <div className="card version-header">
                  <h3>
                    <FaFileAlt /> Version Details {releaseData.name} <span className="version-number">({releaseData.version})</span>
                  </h3>
                </div>

                {/* Información general de la release */}
                <div className="card release-info responsive">
                  <div className="release-info-item">
                    <FaCalendarAlt /> <strong>Release Date:</strong> {formatDate(releaseData.published_at)}
                  </div>
                  <div className="release-info-item">
                    <a href={releaseData.url} target="_blank" rel="noopener noreferrer" className="github-link">
                      <FaGithub /> View on GitHub
                    </a>
                  </div>
                  <div className="release-info-item">
                    <FaFileAlt /> <strong>SHA256:</strong> {releaseData.sha256 || 'Not available'}
                  </div>
                  <div className="release-info-item">
                    <FaCalendarAlt /> <strong>Creation Date:</strong> {formatDate(releaseData.created_at)}
                  </div>
                </div>

                <div className="card-container">
                <div className="card download-chart half-width">
                    <h4>Download Chart</h4>
                    <DownloadChart assets={releaseData.assets} />
                  </div>
                  <div className="card latest-versions half-width">
                    <h4>Latest Versions</h4>
                    {renderLatestVersions()}
                  </div>


                </div>

                <div className="card download-list">
                  <h4 onClick={() => setIsDownloadsVisible(!isDownloadsVisible)} className="download-toggle">
                    {isDownloadsVisible ? <FaCaretUp /> : <FaCaretDown />} Available Downloads
                  </h4>
                  {isDownloadsVisible && (
                    <ul className="download-list-items">
                      {JSON.parse(releaseData.assets).map((asset, index) => (
                        <li key={index} className="download-item">
                          <a href={asset.downloadUrl} target="_blank" rel="noopener noreferrer" className="download-link">
                            {getOSIcon(asset.name)} {asset.name} ({asset.size})
                          </a>
                          <span className="download-count"> - Downloads: {asset.downloadCount}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : (
              <p>Loading version data...</p>
            )}

          <FaTable /> <strong>Currency Data</strong>
            <table className="home-centered-table">
              <thead>
                <tr>
                  <th><FaCogs /> Algorithm</th>
                  <th><FaNetworkWired /> Network Hashrate</th>
                  <th><FaUsers /> Miners</th>
                  <th><FaKey /> Block Height</th>
                  <th><FaClock /> Last Block Mined</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Link to={`/algorithmhardware/${selectedCurrency.algo}`}>
                      {selectedCurrency.algo}
                    </Link>
                  </td>
                  <td>{formatHashrate(selectedCurrency.network_hashrate)}</td>
                  <td>{selectedCurrency.workers}</td>
                  <td>{selectedCurrency.height}</td>
                  <td>
                    {selectedCurrency.height} <br />
                    <span className={`home-highlighted-time ${getTimeColor(Math.floor(Math.min(...selectedCurrency.pools.map(pool => pool.timesincelast)) / 60))}`}>
                      <FaClock /> {getLatestBlockTime(selectedCurrency.pools)}
                    </span>
                    <br />
                    <strong>Pool:</strong> {getPoolNameForLastBlock}
                  </td>
                </tr>
              </tbody>
            </table>

            <h3><FaTable /> Mining Pools</h3>
            <table className="home-centered-table">
              <thead>
                <tr>
                  <th><FaUsers /> Pool</th>
                  <th><FaNetworkWired /> Hashrate</th>
                  <th><FaUsers /> Miners</th>
                  <th><FaNetworkWired /> Network Hashrate</th>
                  <th><FaLock /> Ports (SSL)</th>
                  <th><FaBitcoin /> 24h Blocks</th>
                  <th><FaBitcoin /> 24h BTC</th>
                  <th><FaBitcoin /> Reward</th>
                  <th><FaBitcoin /> Minimum Payout</th>
                  <th><FaBitcoin /> Fees / Solo</th>
                  <th><FaKey /> Block Height</th>
                </tr>
              </thead>
              <tbody>
              {selectedCurrency.pools
  .sort((a, b) => {
    if (b.hashrate !== a.hashrate) {
      return b.hashrate - a.hashrate; // Ordena por hashrate
    }
    return b['24h_blocks'] - a['24h_blocks']; // Ordena por 24h_blocks si hay empate en hashrate
  })
  .map((pool, index) => {
    const networkHashrate = calculateNetworkHashrate(selectedCurrency.pools);
    const percentage = ((pool.hashrate / networkHashrate) * 100).toFixed(2);
    const minutesSinceLastBlock = Math.floor(pool.timesincelast / 60);
    const avgBlockTime = calculateAverageBlockTimeForPool(pool['24h_blocks']);
    const stars = calculateStars(pool['24h_blocks'], avgBlockTime);

    const isTopPool = index < 3;

    return (
      <tr key={index} className={isTopPool ? "highlight-top-pool" : ""}>
        <td>
          <a href={`/pool/${pool.name.toLowerCase()}`}>
            <img
              src={getPoolImage(pool.name)}
              onError={(e) => { e.target.src = '/img/pools/default.webp'; }}
              alt={pool.name}
            />
            {pool.name}
          </a>
        </td>
        <td>{formatHashrate(pool.hashrate)}</td>
        <td>
          {pool.workers === 0 && pool.hashrate > 0 ? (
            <span title="This value is not reported by the pool" style={{ cursor: 'pointer', textDecoration: 'underline', color: 'gray' }}>
              Unknown
            </span>
          ) : (
            pool.workers
          )}
        </td>
        <td>
          {renderNetworkHashrateBar(percentage)}
        </td>
        <td>{pool.port} {pool.ssl_port ? `(SSL: ${pool.ssl_port})` : ''}</td>
        <td title="This is the average time between blocks based on 24 hour blocks">
          {pool['24h_blocks']}
          <br />
          <small style={{ color: 'gray', fontSize: '0.8em' }}>
            <FaClock />
            {avgBlockTime}
          </small>
          <br />
          <small>
            {[...Array(stars)].map((_, i) => (
              <FaStar key={i} style={{ color: getStarColor(stars) }} />
            ))}
            {[...Array(5 - stars)].map((_, i) => (
              <FaStarHalfAlt key={i} style={{ color: 'gray' }} />
            ))}
          </small>
        </td>
        <td>{pool['24h_btc']}</td>
        <td>{pool.reward}</td>
        <td>{pool.minimum_payout}</td>
        <td>{pool.fees || 'N/A'}% / {pool.fees_solo || 'N/A'}%</td>
        <td>
          {pool.lastblock} <br />
          <span className={`home-highlighted-time ${getTimeColor(minutesSinceLastBlock)}`}>
            <FaClock /> {formatTime(pool.timesincelast)}
          </span>
        </td>
      </tr>
    );
  })}

              </tbody>
            </table>

            <div className="tables-container">

              <div className="table-blocks">
                <h3><FaTable /> Recent Blocks</h3>
                <table className="home-centered-table">
                  <thead>
                    <tr>
                      <th><FaKey /> Block Height</th>
                      <th><FaUsers /> Pool</th>
                      <th><FaClock /> Date and Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginateBlocks(blocks).map((block, index) => (
                      <tr key={index}>
                        <td>{block.block_height}</td>
                        <td>
                          {block.pool_name && block.pool_name !== 'Unknown' ? (
                            <a href={`/pool/${block.pool_name.toLowerCase()}`} className="block-pool-link">
                              {block.pool_name}
                            </a>
                          ) : (
                            'Unknown'
                          )}
                        </td>
                        <td>{block.timestamp === 'Unknown' ? 'Unknown' : new Date(block.timestamp).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pagination-controls">
              <button onClick={handlePreviousPage} disabled={currentPage === 1}>
                <FaArrowLeft /> Previous
              </button>
              <span>Page {currentPage} of {Math.ceil(blocks.length / blocksPerPage)}</span>
              <button onClick={handleNextPage} disabled={currentPage === Math.ceil(blocks.length / blocksPerPage)}>
                Next <FaArrowRight />
              </button>
            </div>
              </div>

              <div className="table-markets">
                <h3><FaTable /> Markets</h3>
                <table className="home-centered-table">
                  <thead>
                    <tr>
                      <th><FaExchangeAlt /> Exchange</th>
                      <th><FaHashtag /> Symbol</th>
                      <th><FaDollarSign /> Last Price</th>
                      <th><FaChartLine /> Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCurrency.markets.map((market, index) => (
                      <tr key={index}>
                        <td>{market.exchange}</td>
                        <td>{market.symbol}</td>
                        <td>{market.lastPrice}</td>
                        <td>{market.volume}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <h3>Distribución de Bloques por Pool</h3>
              <Doughnut data={blockDistribution} />
            </div>

          </div>
        ) : (
          <div>
            <p>Selecciona una moneda para ver los detalles.</p>
            <Status />
          </div>
        )}
      </main>
    </div>
  );
}

export default Home;
